import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { rotateAnimation } from '@shared/animations/rotate.anim';
import { Crossfilter } from '@report/shared/services/crossfilter.service';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { LifecycleHooks } from '@shared/services/lifecycle-hooks.service';
import { getLastValue } from '@shared/operators/share-ref.operator';
import { ExecutiveSummary, GridRecommendationCache } from '@shared/models/report.model';
import { Store } from '@ngxs/store';
import { UpdateProperties } from '@shared/states/prefs.actions';
import { ChartsManager } from '@report/shared/services/charts-manager.service';
import { LanguageData2 } from '@shared/models/locale.model';
import { isURL } from 'class-validator';
import { ExtractorApi } from '@shared/services/extractor-api.service';
import { AiApiService } from '@shared/services/ai-api.service';
import { AiPromptConfig, AiPromptFunction, AiRole } from '@shared/models/ai.model';

@Component({
  selector: 'ai-settings',
  templateUrl: './ai-settings.component.html',
  styleUrls: ['./ai-settings.component.scss'],
  animations: [rotateAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AISettings implements OnInit {
  @Input() selectedSurvey: string = '';
  @Input() selectedReportData: any = {};
  @Input() hideExecutiveSummaryAd: boolean = false;

  @Output() updateExecutiveSummary: EventEmitter<any> = new EventEmitter<any>();
  @Output() changesInExecutiveSummary: EventEmitter<boolean> = new EventEmitter<boolean>();

  public userProfileUpdate: Subject<string> = new Subject<string>();
  public userProfileEditing$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public userProfile: string = '';

  public companyProfileLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public companyProfileUpdate: BehaviorSubject<[string, boolean]> = new BehaviorSubject<[string, boolean]>(['', false]);
  public companyProfile: string = '';
  public companyProfileUrl: string = '';

  public themeUpdate: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public mergedSurveyUpdate: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public aiGridChanges: boolean = false;
  public executiveSummaryChanges: boolean = false;
  public hideChangesAvailableAd: boolean = false;
  public showAdditionalContext: boolean = false;
  public previousRespondentCount: number = 0;

  public languages: LanguageData2[] = [];
  public showLanguages: boolean = false;
  public activeLanguage: string = '';

  constructor(
    public cf: Crossfilter,
    private cm: ChartsManager,
    private cdRef: ChangeDetectorRef,
    private ea: ExtractorApi,
    private ai: AiApiService,
    private hooks: LifecycleHooks,
    private store: Store,
  ) {}

  ngOnInit(): void {
    this.cf.crossfilter.pipe(takeUntil(this.hooks.destroy)).subscribe(() => {
      const respondents = this.cf.getRespondentCounts()?.total;

      if (getLastValue(this.cf.aiAnalysisEnabledStatus) && respondents !== this.previousRespondentCount) {
        this.userProfileUpdate.next(this.userProfile);
        this.previousRespondentCount = respondents;
      }

      this.languages = Object.values(this.cf.getLocaleConfig());
      this.showLanguages = this.languages && Object.keys(this.languages).length > 1;
      this.activeLanguage = this.cf.getActiveLocale();
      this.themeUpdate.next(
        !!(
          this.cf.getExecutiveSummary()?.themes &&
          this.cf.getThemesFromWhyFinders() &&
          this.cf.getExecutiveSummary()?.themes !== this.cf.getThemesFromWhyFinders()
        ),
      );
      this.mergedSurveyUpdate.next(
        !!(
          (this.cf.getExecutiveSummary()?.mergedSurveys || this.cf.getMergedSurveyNames()) &&
          this.cf.getExecutiveSummary()?.mergedSurveys !== this.cf.getMergedSurveyNames()
        ),
      );
    });

    combineLatest([this.userProfileUpdate, this.companyProfileUpdate, this.themeUpdate, this.mergedSurveyUpdate])
      .pipe(
        debounceTime(150),
        map(
          ([
            userProfileChange,
            [companyProfileChange, clearedCompanyProfile],
            themeUpdate,
            mergedSurveysUpdate,
          ]: any) => {
            const gridRecommendation: GridRecommendationCache = this.cf.getGridRecommendation();
            const executiveSummary: ExecutiveSummary = this.cf.getExecutiveSummary();
            const respondents: number = this.cf.getRespondentCounts()?.total;

            return {
              aiGridChanges:
                ((gridRecommendation?.userProfile || userProfileChange) &&
                  userProfileChange !== gridRecommendation?.userProfile) ||
                (respondents && gridRecommendation?.respondents && respondents !== gridRecommendation?.respondents) ||
                ((gridRecommendation?.companyProfile || companyProfileChange) &&
                  companyProfileChange !== gridRecommendation?.companyProfile),
              executiveSummaryChanges:
                ((executiveSummary?.userProfile || userProfileChange) &&
                  userProfileChange !== executiveSummary?.userProfile) ||
                (respondents && executiveSummary?.respondents && respondents !== executiveSummary?.respondents) ||
                ((executiveSummary?.companyProfile || companyProfileChange) &&
                  companyProfileChange !== executiveSummary?.companyProfile) ||
                themeUpdate ||
                mergedSurveysUpdate,
              change: userProfileChange,
              gridUserProfile: gridRecommendation?.userProfile,
              gridCompanyProfile: gridRecommendation?.companyProfile,
              clearedCompanyProfile,
            };
          },
        ),
        takeUntil(this.hooks.destroy),
      )
      .subscribe((result) => {
        if (!result.clearedCompanyProfile && !this.companyProfile && result.gridCompanyProfile) {
          this.companyProfile = result.gridCompanyProfile;
          this.companyProfileUpdate.next([this.companyProfile, false]);
        }
        if (!this.userProfile && result.gridUserProfile) {
          this.userProfile = result.gridUserProfile;
          this.userProfileUpdate.next(this.userProfile);
        } else {
          this.aiGridChanges = result.aiGridChanges;
          this.executiveSummaryChanges = result.executiveSummaryChanges;
          this.changesInExecutiveSummary.emit(this.executiveSummaryChanges);
          this.cdRef.markForCheck();
        }
      });

    // This is for Updating user profile after initial AI analysis load
    this.cf.aiAnalysisEnabledStatus
      .pipe(
        takeUntil(this.hooks.destroy),
        distinctUntilChanged(),
        filter(Boolean),
        switchMap(() => this.cf.aiLoading),
      )
      .subscribe((loading) => {
        if (!loading && !this.userProfile) {
          this.userProfileUpdate.next(this.userProfile);
        }
      });
  }

  public detectExpandNeed(value: string) {
    if (value?.length > 70 && value.trim().includes(' ')) {
      this.companyProfile = value;
      this.cdRef.detectChanges();
    }
  }

  public updateContext(value: string) {
    value = value?.trim();
    const isUrl = isURL(value);
    if (!value) {
      this.companyProfile = '';
      return;
    } else if (isUrl) {
      this.companyProfileLoading$.next(true);
      this.extractText(value).subscribe((res) => {
        this.companyProfileLoading$.next(false);
        if (res) {
          this.companyProfile = res;
          this.companyProfileUpdate.next([this.companyProfile, true]);
          this.cdRef.detectChanges();
        }
      });
    } else {
    }
  }

  private extractText(url: string): Observable<any> {
    const language = this.languages?.find((lang) => lang.code === this.activeLanguage)?.name || 'English';
    const metaRequest = (response) =>
      response?.content
        ? this.ai.promptConfig(
            AiPromptConfig.GetWebsiteMetaInfo,
            [{ role: AiRole.User, message: response.content.slice(0, 8000) }],
            {
              modelKey: 'gpt-4o-mini',
              functionName: AiPromptFunction.GetWebsiteMetaInfo,
              maxTokens: 512,
            },
          )
        : of({});
    const updateCompanyInfo = (response) =>
      metaRequest(response).pipe(
        switchMap((meta: any) => (meta?.companyName ? of(meta) : metaRequest(response))),
        switchMap((meta: any) =>
          meta?.companyName
            ? this.ai.promptConfig(
                AiPromptConfig.SummarizeCompanyInfo,
                [{ role: AiRole.User, message: response.content.slice(0, 8000) }],
                {
                  modelKey: 'gpt-4o-latest',
                  parameters: { system: [language, meta?.companyName || ''] },
                  maxTokens: 1024,
                },
              )
            : of(''),
        ),
        catchError(() => of('')),
      );

    return this.ea.extract(url, 'text').pipe(switchMap(updateCompanyInfo));
  }

  public showExecutiveSummaryButton(): boolean {
    return !Object.keys(this.cf.getExecutiveSummary()?.content || {})?.length;
  }

  public regenerateGrid(userProfile?: string) {
    if (userProfile) {
      this.userProfile = userProfile;
      this.userProfileEditing$.next(false);
    }

    this.aiGridChanges = false;
    this.hideChangesAvailableAd = true;
    this.cdRef.markForCheck();
    const data = {
      description: this.userProfile || '',
      orgDescription: this.companyProfile || '',
    };
    this.store.dispatch(new UpdateProperties(data));
    this.cm.reset();
    this.cf.softResetCrossfilter();
    this.cm.connect([]);
  }

  public isOtherLanguage(language: LanguageData2 | string): boolean {
    return (typeof language === 'string' ? language : language?.code)?.length > 2;
  }

  public changeLanguage($event: string) {
    this.cf.changeLocale($event);
  }
}
